import { store } from "@store/store";
import type { Company } from "@types";
import { type LoaderFunctionArgs, redirect } from "react-router";
import { getPostHog } from "@/analytics/posthog";
import { buildCompanyPath } from "@/hooks/companyPath";
import { classifyCompanyBySlugError } from "@/services/companyBySlugError";
import { defaultPath } from "@/services/getPostLoginPath";
import { companyBySlugQueryOptions } from "@/services/useGetCompanyBySlug";
import { queryClient } from "../queryClient";
import { checkAuth } from "./authLoader";
import {
  clearSlugUnresolvable,
  markSlugUnresolvable,
} from "./slugResolutionState";
import { SUPER_PATHS } from "./superRoutes";

/**
 * Loader for the `/:slug` layout route — the heart of the slug routing
 * scheme (SD-11894/SD-11897).
 *
 * Resolves the URL slug to a company via `GET /c/{slug}` (cached, shared with
 * `useGetCompanyBySlug`) and returns it to the committed shell route. The shell
 * then seeds the active company and primes the id-keyed company-detail cache:
 * the URL is now the source of truth for tenant identity, and
 * `localStorage.companyId` is demoted to a last-used hint.
 *
 * Auth itself is enforced by the parent `authLoader` (which runs in parallel on
 * the gate above this route); RBAC / "Forbidden" is enforced by the shell's
 * `userIsAuthorized` selector (now prefix-aware). This loader owns exactly one
 * concern — turning a slug into tenant data — and fails safe: an
 * unresolvable slug (unknown, no access, or a transient blip) bounces to the
 * company picker rather than rendering a shell with no valid tenant.
 */
export type CompanySlugLoaderData = {
  company: Company;
};

export const companySlugLoader = async ({
  params,
  request,
}: LoaderFunctionArgs) => {
  // This loader is attached to `/:slug`, so `params.slug` is the decoded slug
  // (and is basename-safe, unlike parsing the raw request URL). The param name
  // matches `useCompanyPath`'s `useParams().slug` so the PR1 hook reads it.
  const slug = params.slug;

  const { session } = store.getState();
  const { accessToken, user } = session;
  const accountId = user?.accountId;
  const recoveryPath =
    user?.role === "super"
      ? SUPER_PATHS.root
      : user?.role === "owner"
        ? "/company-selection"
        : defaultPath(user);

  // Auth/account not ready (or the token just expired): defer to the parent
  // authLoader — which runs in parallel and owns the /login redirect — instead
  // of firing a doomed `/c/{slug}` request. `checkAuth` also fail-closes an
  // expired session, so we never send `Bearer <expired>` or resolve against a
  // missing account scope.
  if (!slug || !accessToken || !accountId || !checkAuth()) {
    return null;
  }

  let company: Company;
  const slugQueryOptions = companyBySlugQueryOptions(
    slug,
    accessToken,
    accountId,
  );
  try {
    company = await queryClient.ensureQueryData({
      ...slugQueryOptions,
      revalidateIfStale: true,
    });
  } catch (error) {
    // A newer navigation superseded this one (react-router aborted the signal):
    // don't run recovery for a lookup the user already moved on from — the same
    // guard the success path applies before committing.
    if (request.signal.aborted) {
      return null;
    }
    const kind = classifyCompanyBySlugError(error);
    if (kind === "error") {
      getPostHog()?.captureException(error);
      const cachedCompany = queryClient.getQueryData<Company>(
        slugQueryOptions.queryKey,
      );
      if (cachedCompany) {
        company = cachedCompany;
      } else {
        // Mark the slug unresolvable so authLoader's redirect won't bounce the
        // recovery straight back into this same failing `/:slug` (SD-11900).
        markSlugUnresolvable(slug);
        return redirect(recoveryPath);
      }
    } else {
      // 404 (unknown slug) / 403 (no access): bounce to a REACHABLE recovery
      // rather than keep a stale access verdict alive. Marking the slug stops
      // authLoader from re-slugifying the recovery back into the same rejected
      // slug (stale cache / renamed / revoked) — the loop-breaker (SD-11900).
      markSlugUnresolvable(slug);
      return redirect(recoveryPath);
    }
  }

  // Bail if a newer navigation superseded this one while the resolve was in
  // flight: react-router aborts the stale loader's signal, so returning alpha's
  // company after the user already moved on to `/beta` would let the committed
  // shell apply the wrong tenant.
  if (request.signal.aborted) {
    return null;
  }

  // Resolved (fresh or from cache): clear any prior failure mark so authLoader's
  // redirect is re-enabled for this slug (the loop-breaker self-heals here).
  clearSlugUnresolvable(slug);
  return { company } satisfies CompanySlugLoaderData;
};

/**
 * Index of the `/:slug` layout. The bare company root has no page of its own
 * (the deep routes below this mount carry paths, so they do not match the bare
 * root — which would otherwise render an empty shell). Send the user to their
 * role default landing UNDER the slug
 * (e.g. `/acme/operator/chat`); `companySlugLoader` on the parent resolves the slug
 * data for the committed shell.
 */
export const companyRootRedirect = ({ params }: LoaderFunctionArgs) => {
  const { user } = store.getState().session;
  return redirect(buildCompanyPath(params.slug, defaultPath(user)));
};
